/**
 * Display labels for the cross-tenant access roles (XT-D22/D24).
 *
 * TNCY-6b promoted this from `features/projects/access/roleLabels.ts`; TNCY-9
 * promotes it into the cross-app `@aeriosiq/ownership` package so every
 * merged cross-tenant list (Projects, Contracts, Contract Positions,
 * Inspection Instructions, Data Acquisitions) shares one role vocabulary for
 * the `<OwnershipBadge>` tooltip. The wire enum is lower_snake
 * (`project_customer`, …); the UI shows a human label + a one-line surface
 * hint.
 *
 * `AccessRole` is the `tenancy/v1` `AccessRole` enum, re-exported here from
 * the generated `@aeriosiq/types` so this package owns its own type
 * surface without reaching into an app's local feature module.
 */
import type { TenancyComponents } from "@aeriosiq/types";

/** The `project_role_access_map` role enum (XT-D24), from `tenancy/v1`. */
export type AccessRole = TenancyComponents["schemas"]["AccessRole"];

export const ACCESS_ROLE_LABEL: Record<AccessRole, string> = {
  project_customer: "Customer",
  project_contractor: "Contractor",
  contract_customer: "Customer (contract)",
  contract_contractor: "Contractor (contract)",
  reporting_subcontractor: "Reporting subcontractor",
  ii_subcontractor: "On-site subcontractor",
};

/** One-line surface summary (XT-D22) — used as a picker/grant hint. */
export const ACCESS_ROLE_SURFACE: Record<AccessRole, string> = {
  project_customer: "Reads project state, progress and published reports only",
  project_contractor: "Works the project — IIs, data acquisitions, reports",
  contract_customer: "Reads their contract's reports only",
  contract_contractor: "Works only their contract",
  reporting_subcontractor: "Reads IIs, evaluates data, writes reports",
  ii_subcontractor: "Works the inspection instructions assigned on-site",
};

export function accessRoleLabel(role: AccessRole): string {
  return ACCESS_ROLE_LABEL[role];
}
